// Registry of refetchers for every mounted post-counts hook. Used to
// imperatively refresh counts after actions that bypass Apollo (e.g. the
// legacy composer RPC save), where no Pusher event reaches the originating
// client and the persistent module-level cache would otherwise stay stale.
//
// Lives outside the AllChannels page so non-page callers (notably the legacy
// composer) don't have to deep-import a page internal to trigger a refresh.

type PostCountsFetcher = () => Promise<unknown>

const activeFetchers = new Set<PostCountsFetcher>()

/**
 * Register a mounted post-counts fetcher. Returns an unregister function to
 * call on unmount.
 */
export const registerPostCountsFetcher = (
  fetch: PostCountsFetcher,
): (() => void) => {
  activeFetchers.add(fetch)
  return (): void => {
    activeFetchers.delete(fetch)
  }
}

/**
 * Refetch channel post counts for all mounted consumers.
 *
 * Call this after creating/updating a post through a path that does not run a
 * Pusher event for the current client (notably the composer's legacy RPC save),
 * so sidebar/nav queue & draft count badges update without a page refresh.
 *
 * Residual gap: this only refreshes consumers mounted at call time. The
 * persistent post-counts cache itself is not invalidated, so a consumer that
 * unmounts and remounts after an RPC save still reads the stale cached entry.
 * Proper fix is cache invalidation; deferred because the cache lives inside the
 * shared makeUseGlobalStateHook util (wider blast radius). See CORE-4985.
 */
export const refetchAllPostCounts = (): void => {
  for (const fetch of activeFetchers) {
    void fetch()
  }
}
